import React, { useCallback, useEffect, useState } from "react";
import { Eye, LayoutGrid, Pause, Pencil, Play, Plus, Table2, Trash2 } from "lucide-react";
import {
  createSchedule, deleteSchedule, listScheduleMessages, listSchedules, listScheduleTools, listScheduleTypes, updateSchedule,
} from "../../../api/schedulesApi";
import CustomScheduleFields, { RECIPIENTS, customFromForm, customToForm } from "./CustomScheduleFields";
import { getNotificationErrorMessage } from "../../../api/whatsappNotificationsApi";
import { Alert, Field, Modal, btnPrimary, btnSecondary, card, inputClass } from "../scheduling/ui";

const iconBtn = "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-[#096dea]";
const iconDanger = "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50";

const RUN_STATUS = {
  running: "Running", completed: "Completed", missed: "Missed (trigger down)", skipped: "Skipped", failed: "Failed",
};
const MESSAGE_REASONS = {
  no_parent_guardian_whatsapp_id: "No parent / guardian WhatsApp ID", ambiguous_contacts: "Ambiguous contacts",
  patient_dnd: "Patient on DND", recipient_dnd: "Recipient on DND",
  template_not_configured: "Template not configured", evaluating: "Evaluation interrupted",
  no_whatsapp_id: "No WhatsApp ID",
  no_primary_contact: "No primary contact", ambiguous_primary_contacts: "Multiple primary contacts",
  no_primary_contact_whatsapp_id: "Primary contact has no WhatsApp ID",
  primary_contact_template_not_configured: "Primary-contact template not configured",
};

const formatDateTime = (iso, timeZone) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", { timeZone, dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

function lastRunText(row) {
  if (!row.last_run_status) return "Never run";
  const label = RUN_STATUS[row.last_run_status] || row.last_run_status;
  const r = row.last_run_result || {};
  const counts = ["queued", "skipped", "failed"].filter((key) => r[key] != null).map((key) => `${r[key]} ${key}`).join(", ");
  return [label, r.run_date, counts || (r.reason ? r.reason.replaceAll("_", " ") : ""), r.error].filter(Boolean).join(" · ");
}

const emptyForm = (timezone) => ({
  name: "", schedule_type: "custom", enabled: true, timezone, run_times: ["11:00"],
  tool_id: null, custom: customToForm(null),
});
const toForm = (row) => ({
  ...row, run_times: [...row.run_times], tool_id: row.tool_id || null,
  custom: customToForm(row.schedule_type === "custom" ? row.config : null),
});
function ScheduleForm({ open, initial, tools, botId, defaultTimezone, onClose, onSave }) {
  const [form, setForm] = useState(() => (initial ? toForm(initial) : emptyForm(defaultTimezone)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) { setForm(initial ? toForm(initial) : emptyForm(defaultTimezone)); setError(""); }
  }, [open, initial, defaultTimezone]);
  const isCustom = form.schedule_type === "custom";
  const set = (patch) => setForm((previous) => ({ ...previous, ...patch }));
  const setTime = (index, value) => set({ run_times: form.run_times.map((time, i) => (i === index ? value : time)) });
  const submit = async (event) => {
    event.preventDefault();
    let config = initial?.config || {};
    if (isCustom) {
      const [customConfig, errors] = customFromForm(form.custom, form.tool_id);
      if (errors.length) { setError(errors.join(" ")); return; }
      config = customConfig;
    }
    setSaving(true); setError("");
    try {
      await onSave({
        name: form.name, schedule_type: form.schedule_type, enabled: form.enabled,
        timezone: form.timezone, run_times: form.run_times, config, tool_id: isCustom ? form.tool_id : null,
      });
    } catch (err) {
      setError(getNotificationErrorMessage(err, "Could not save schedule."));
    } finally {
      setSaving(false);
    }
  };
  return <Modal open={open} wide={isCustom} title={initial ? "Edit schedule" : "Add schedule"} onClose={onClose}>
    <form aria-label="Schedule form" onSubmit={submit} className="space-y-4">
      <Alert>{error}</Alert>
      <Field label="Name">
        <input className={inputClass} value={form.name} maxLength={120} required onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field label="Timezone" hint="IANA name, for example Asia/Kolkata.">
        <input className={inputClass} value={form.timezone} required onChange={(e) => set({ timezone: e.target.value })} />
      </Field>
      <fieldset className="space-y-2">
        <legend className="text-xs font-semibold text-slate-600">Run times (local)</legend>
        {form.run_times.map((time, index) => <div key={index} className="flex items-center gap-2">
          <input type="time" aria-label={`Run time ${index + 1}`} className={`${inputClass} mt-0`} value={time} required onChange={(e) => setTime(index, e.target.value)} />
          {form.run_times.length > 1 && <button type="button" className={btnSecondary} aria-label={`Remove run time ${index + 1}`}
            onClick={() => set({ run_times: form.run_times.filter((_, i) => i !== index) })}><Trash2 size={14} /></button>}
        </div>)}
        {form.run_times.length < 6 && <button type="button" className={btnSecondary} onClick={() => set({ run_times: [...form.run_times, "14:00"] })}>
          <Plus size={14} /> Add run time
        </button>}
        <p className="text-[11px] text-slate-400">Later runs on the same day retry skipped or failed customers. Already queued customers are never messaged twice.</p>
      </fieldset>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Enabled
      </label>
      {isCustom && <CustomScheduleFields botId={botId} tools={tools} toolId={form.tool_id} timezone={form.timezone}
        onToolChange={(toolId) => set({ tool_id: toolId })} custom={form.custom} onChange={(custom) => set({ custom })} />}
      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
        <button type="submit" className={btnPrimary} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
      </div>
    </form>
  </Modal>;
}

function Detail({ label, children }) {
  return <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt><dd className="mt-0.5 text-sm text-slate-800">{children}</dd></div>;
}

function ScheduleDetail({ botId, schedule, typeLabel, toolName, onClose, onEdit }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [runDate, setRunDate] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (!schedule) return undefined;
    let active = true;
    setRows(null); setError("");
    listScheduleMessages(botId, schedule.id, { run_date: runDate || undefined, status: status || undefined, limit: 200 })
      .then((data) => { if (active) setRows(data); })
      .catch((err) => { if (active) setError(getNotificationErrorMessage(err, "Could not load messages.")); });
    return () => { active = false; };
  }, [botId, schedule, runDate, status]);
  if (!schedule) return null;
  return <Modal open wide title={schedule.name} onClose={onClose}
    footer={<><button type="button" className={btnSecondary} onClick={onClose}>Close</button>
      <button type="button" className={btnPrimary} onClick={onEdit}><Pencil size={14} /> Edit</button></>}>
    <dl className="grid grid-cols-2 gap-4 rounded-xl bg-slate-50 p-4">
      <Detail label="Type">{typeLabel(schedule.schedule_type)}</Detail>
      <Detail label="Status">{schedule.enabled ? "Enabled" : "Paused"}</Detail>
      <Detail label="Run times">{schedule.run_times.join(", ")}</Detail>
      <Detail label="Timezone">{schedule.timezone}</Detail>
      <Detail label="Next run">{schedule.enabled ? formatDateTime(schedule.next_run_at, schedule.timezone) : "—"}</Detail>
      <Detail label="Last run">{formatDateTime(schedule.last_run_at, schedule.timezone)}</Detail>
      {schedule.schedule_type === "custom" && <>
        <Detail label="Selector tool">{toolName(schedule.tool_id)}</Detail>
        <Detail label="Message occurrence">{schedule.config?.occurrence_mode === "selector" ? "Selector occurrence key" : "Daily"}</Detail>
        <Detail label="Send to">{RECIPIENTS.find((item) => item.value === schedule.config?.recipient)?.label || "—"}</Detail>
        <Detail label="Template">{schedule.config?.template ? `${schedule.config.template.template_name} (${schedule.config.template.language_code})` : "—"}</Detail>
        <Detail label="Primary-contact template">{schedule.config?.primary_contact_template?.template_name || "Not configured (optional)"}</Detail>
        <Detail label="Respect customer DND">{schedule.config?.respect_dnd !== false ? "Yes" : "No (DND overridden)"}</Detail>
      </>}
      <div className="col-span-2"><Detail label="Last run result">{lastRunText(schedule)}</Detail></div>
    </dl>
    <h4 className="text-sm font-bold text-slate-900">Messages</h4>
    <div className="flex flex-wrap gap-3">
      <Field label="Date"><input type="date" className={inputClass} value={runDate} onChange={(e) => setRunDate(e.target.value)} /></Field>
      <Field label="Status">
        <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option><option value="queued">Queued</option><option value="skipped">Skipped</option><option value="failed">Failed</option>
        </select>
      </Field>
    </div>
    <Alert>{error}</Alert>
    {!rows && !error && <p role="status">Loading messages...</p>}
    {rows && !rows.length && <p className="text-sm text-slate-500">No messages for this filter.</p>}
    {rows && rows.length > 0 && <table className="w-full text-left text-sm">
      <thead className="text-xs uppercase text-slate-500"><tr><th className="py-2">Date</th><th>Customer</th><th>Recipient</th><th>Template</th><th>Status</th><th>Delivery</th></tr></thead>
      <tbody>{rows.map((row) => <tr key={row.id} className="border-t border-slate-100">
        <td className="py-2">{row.occasion_date}</td>
        <td>{row.customer_name || row.customer_id}</td>
        <td>{row.recipient_name || "—"}</td>
        <td>{row.template_name || "—"}</td>
        <td>{row.status}{row.reason ? ` · ${MESSAGE_REASONS[row.reason] || row.reason}` : ""}</td>
        <td>{row.delivery_status || "—"}</td>
      </tr>)}</tbody>
    </table>}
  </Modal>;
}

export default function SchedulesPanel({ botId, defaultTimezone = "Asia/Kolkata" }) {
  const [rows, setRows] = useState(null);
  const [types, setTypes] = useState([]);
  const [tools, setTools] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  const load = useCallback(() => Promise.all([
    listSchedules(botId), listScheduleTypes(botId), listScheduleTools(botId),
  ])
    .then(([schedules, scheduleTypes, selectorTools]) => {
      setRows(schedules); setTypes(scheduleTypes); setTools(selectorTools || []); setError("");
    })
    .catch((err) => setError(getNotificationErrorMessage(err, "Could not load schedules."))), [botId]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const typeLabel = (type) => types.find((item) => item.type === type)?.label || type;
  const toolName = (id) => (id ? tools.find((tool) => tool.id === id)?.name || `${id} (unavailable)` : "—");
  const run = async (action, fallback) => {
    try { await action(); await load(); } catch (err) { setError(getNotificationErrorMessage(err, fallback)); }
  };
  const save = async (body) => {
    if (editing === "new") await createSchedule(botId, body);
    else await updateSchedule(botId, editing.id, { ...body, revision: editing.revision });
    setEditing(null);
    await load();
  };
  const toggle = (row) => run(() => updateSchedule(botId, row.id, {
    name: row.name, schedule_type: row.schedule_type, enabled: !row.enabled, timezone: row.timezone,
    run_times: row.run_times, config: row.config, tool_id: row.tool_id || null, revision: row.revision,
  }), "Could not update schedule.");
  const remove = (row) => {
    if (window.confirm(`Delete schedule "${row.name}"?`)) run(() => deleteSchedule(botId, row.id, row.revision), "Could not delete schedule.");
  };
  const actions = (row) => <div className="inline-flex gap-1">
    <button type="button" className={iconBtn} title="View" aria-label={`View ${row.name}`} onClick={() => setViewing(row)}><Eye size={15} /></button>
    <button type="button" className={iconBtn} title="Edit" aria-label={`Edit ${row.name}`} onClick={() => setEditing(row)}><Pencil size={15} /></button>
    <button type="button" className={iconBtn} title={row.enabled ? "Pause" : "Resume"} aria-label={`${row.enabled ? "Pause" : "Resume"} ${row.name}`} onClick={() => toggle(row)}>
      {row.enabled ? <Pause size={15} /> : <Play size={15} />}
    </button>
    <button type="button" className={iconDanger} title="Delete" aria-label={`Delete ${row.name}`} onClick={() => remove(row)}><Trash2 size={15} /></button>
  </div>;
  const status = (row) => row.enabled
    ? <span className="text-emerald-700">Enabled</span>
    : <span className="text-slate-500">Paused</span>;

  return <section className={`${card} space-y-4`} aria-label="Schedules">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h3 className="text-base font-bold text-slate-900">Schedules</h3>
        <p className="text-sm text-slate-500">Recurring WhatsApp messages. The scheduler trigger checks every 10 minutes and runs what is due.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Schedule view" className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {[["card", "Card view", LayoutGrid], ["table", "Table view", Table2]].map(([mode, label, Icon]) => (
            <button key={mode} type="button" onClick={() => setViewMode(mode)} aria-label={label} title={label}
              aria-pressed={viewMode === mode}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${viewMode === mode ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon size={16} />
            </button>
          ))}
        </div>
        <button type="button" className={btnPrimary} onClick={() => setEditing("new")} disabled={!types.some((type) => type.type === "custom" && type.available)}><Plus size={16} /> Add schedule</button>
      </div>
    </div>
    <Alert>{error}</Alert>
    {!rows && !error && <p role="status">Loading schedules...</p>}
    {rows && !rows.length && <p className="text-sm text-slate-500">No schedules yet. Add one to start sending.</p>}
    {rows && rows.length > 0 && viewMode === "card" && <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {rows.map((row) => <article key={row.id} aria-label={row.name} className="flex min-w-0 flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <button type="button" onClick={() => setViewing(row)} className="break-words text-left text-sm font-bold text-slate-900 hover:text-[#096dea]">{row.name}</button>
            <p className="text-xs text-slate-500">{typeLabel(row.schedule_type)}</p>
          </div>
          <div className="text-xs font-semibold">{status(row)}</div>
        </div>
        <dl className="space-y-3 text-sm">
          <div><dt className="text-xs font-semibold text-slate-500">Run times</dt><dd>{row.run_times.join(", ")}<span className="block text-xs text-slate-400">{row.timezone}</span></dd></div>
          <div><dt className="text-xs font-semibold text-slate-500">Next run</dt><dd>{row.enabled ? formatDateTime(row.next_run_at, row.timezone) : "—"}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500">Last run</dt><dd className="break-words">{lastRunText(row)}</dd></div>
        </dl>
        <div className="mt-auto flex justify-end border-t border-slate-100 pt-3">{actions(row)}</div>
      </article>)}
    </div>}
    {rows && rows.length > 0 && viewMode === "table" && <div className="overflow-x-auto"><table className="w-full text-left text-sm">
      <thead className="text-xs uppercase text-slate-500"><tr>
        <th className="py-2">Name</th><th>Type</th><th>Run times</th><th>Status</th><th>Next run</th><th>Last run</th><th className="text-right">Actions</th>
      </tr></thead>
      <tbody>{rows.map((row) => <tr key={row.id} className="cursor-pointer border-t border-slate-100 align-top hover:bg-slate-50" onClick={(e) => { if (!e.target.closest("button")) setViewing(row); }}>
        <td className="py-2 font-semibold text-slate-800">{row.name}</td>
        <td>{typeLabel(row.schedule_type)}</td>
        <td>{row.run_times.join(", ")}<span className="block text-[11px] text-slate-400">{row.timezone}</span></td>
        <td>{status(row)}</td>
        <td>{row.enabled ? formatDateTime(row.next_run_at, row.timezone) : "—"}</td>
        <td className="max-w-[16rem]">{lastRunText(row)}</td>
        <td className="whitespace-nowrap text-right">
          {actions(row)}
        </td>
      </tr>)}</tbody>
    </table></div>}
    <ScheduleForm open={Boolean(editing)} initial={editing === "new" ? null : editing} tools={tools} botId={botId}
      defaultTimezone={defaultTimezone} onClose={() => setEditing(null)} onSave={save} />
    <ScheduleDetail botId={botId} schedule={viewing} typeLabel={typeLabel} toolName={toolName} onClose={() => setViewing(null)}
      onEdit={() => { setEditing(viewing); setViewing(null); }} />
  </section>;
}
